import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Check, Loader2 } from "lucide-react";
import { Footer } from "@/components/landing/Footer";
import { SocialLinks } from "@/components/landing/SocialLinks";
import { PageHeader } from "@/components/landing/PageHeader";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const input = "w-full h-13 py-4 px-5 rounded-2xl bg-[#faf9f6] border border-[#e3e2dc] text-sm outline-none focus:border-[#ff2a85]/60 transition-colors";
const blank = { name: "", email: "", subject: "", message: "" };

export default function Contact() {
  const [params] = useSearchParams();
  const ref = params.get("ref");
  const [f, setF] = useState({
    ...blank,
    email: params.get("email") || "",
    subject: params.get("subject") || "",
    message: ref ? `Hi, I'm writing about my order #${ref}.\n\n` : "",
  });
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setState("sending");
    setError("");
    try {
      await axios.post(`${API}/contact`, f);
      setState("done");
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : Array.isArray(d) ? d.map((x) => x.msg).join(" ") : "Could not send. Try again.");
      setState("idle");
    }
  };

  return (
    <main data-testid="contact-page" className="bg-[#ffffff] text-[#0a0a0b] min-h-screen antialiased">
      <PageHeader page="contact" link={{ id: "track", to: "/track-order", label: "Track order" }} />
      <section className="relative pt-36 pb-24 px-5 sm:px-8 overflow-hidden">
        <div className="absolute inset-0 hero-radial" />
        <div className="relative max-w-[1100px] mx-auto grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="font-mono-d text-[11px] uppercase tracking-[0.3em] text-[#ff2a85] mb-6 flex items-center gap-3"><span className="diamond" /> Contact</p>
            <h1 className="font-display font-extrabold uppercase leading-[0.9] text-4xl sm:text-5xl lg:text-6xl tracking-tight">Talk to <span className="text-pink">the District</span></h1>
            <p className="mt-7 text-[#5f5e5a] text-base sm:text-lg leading-relaxed max-w-md">Sizing, orders, collabs, wholesale or just salaams — send us a note and we'll reply by email.</p>
            <Link to="/wholesale" data-testid="contact-wholesale-link" className="mt-6 inline-flex items-center gap-2 rounded-full border border-[#e3e2dc] hover:border-[#0a0a0b] px-4 py-2.5 font-mono-d text-[10px] uppercase tracking-[0.2em] transition-colors">Want to stock Deen District? Apply here</Link>
            <p className="mt-8 font-mono-d text-[10px] uppercase tracking-[0.25em] text-[#5f5e5a]">Or find us on</p>
            <SocialLinks prefix="contact-body" className="mt-3" />
          </div>
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }} className="lg:col-span-7">
            {state === "done" ? (
              <div data-testid="contact-success" className="bg-[#faf9f6] border border-[#e3e2dc] rounded-3xl p-10 text-center">
                <div className="h-14 w-14 mx-auto rounded-full bg-[#ff2a85] text-white flex items-center justify-center"><Check strokeWidth={3} /></div>
                <h2 className="font-display font-extrabold uppercase text-2xl mt-6">Message received</h2>
                <p className="text-[#5f5e5a] text-sm mt-3">We'll get back to you at {f.email}. Deen over Dunya.</p>
              </div>
            ) : (
              <form data-testid="contact-form" onSubmit={submit} className="bg-[#faf9f6]/60 border border-[#e3e2dc] rounded-3xl p-6 sm:p-8 grid sm:grid-cols-2 gap-4">
                <input data-testid="contact-name" className={input} required minLength={2} placeholder="Your name" value={f.name} onChange={set("name")} />
                <input data-testid="contact-email" className={input} required type="email" placeholder="Email" value={f.email} onChange={set("email")} />
                <input data-testid="contact-subject" className={`${input} sm:col-span-2`} placeholder="Subject" value={f.subject} onChange={set("subject")} />
                <textarea data-testid="contact-message" className={`${input} sm:col-span-2 h-40 resize-none`} required minLength={5} placeholder="Your message" value={f.message} onChange={set("message")} />
                {error && <p data-testid="contact-error" className="sm:col-span-2 text-xs text-[#ff2a85] font-mono-d uppercase tracking-wider">{error}</p>}
                <button data-testid="contact-submit" type="submit" disabled={state === "sending"} className="sm:col-span-2 h-14 rounded-full bg-[#ff2a85] hover:bg-[#e60067] text-white font-mono-d text-[11px] uppercase tracking-[0.2em] disabled:opacity-60 flex items-center justify-center gap-2">
                  {state === "sending" ? <Loader2 size={16} className="animate-spin" /> : "Send message"}
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </section>
      <Footer />
    </main>
  );
}
